import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ProfileCard from "../components/ProfileCard";
import VibeTag from "../components/VibeTag";
import { VIBE_TAGS } from "../data/mockData";
import { track } from "../lib/analytics";
import { getToken } from "../lib/auth";

export default function Discovery() {
  const [filter, setFilter] = useState("All");
  const [shortlist, setShortlist] = useState([]);
  const [showPresencePrompt, setShowPresencePrompt] = useState(false);
  const [prospects, setProspects] = useState([]);
  useEffect(() => {
    const token = getToken();
    const headers = token ? { Authorization: "Bearer " + token } : {};
    if (token) {
      fetch("/api/me/discovery", { method: "PATCH", headers }).catch(() => {});
    }
    fetch("/api/prospects", { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.length) setProspects(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // R3: set discovery visited after a 3-second dwell — more meaningful than
    // instant mount. The timer is cancelled cleanly if the user leaves early.
    // TODO: upgrade to an interaction signal (e.g. first filter tap / card hover)
    const timer = setTimeout(() => {
      localStorage.setItem("bm_discovery_visited", "true");
      // Auto-show the Presence intro once — guarded by bm_presence_prompt_seen
      if (localStorage.getItem("bm_presence_prompt_seen") !== "true") {
        setShowPresencePrompt(true);
        localStorage.setItem("bm_presence_prompt_seen", "true");
        track("presence_prompt_impression");
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const filtered = useMemo(() => {
    if (filter === "All") return prospects;
    return prospects.filter((p) => p.tags.includes(filter));
  }, [filter, prospects]);

  const navigate = useNavigate();
  const toggle = (id) =>
    setShortlist((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : s.length < 7 ? [...s, id] : s
    );
  const canContinue = shortlist.length === 7;

  const isEmpty = prospects.length === 0;


  return (
    <AppShell>
        {isEmpty && (
          <div className="px-6 md:px-12 py-20 text-center">
            <h2 className="text-2xl font-display mb-3">No matches yet</h2>
            <p className="text-white/60 mb-6">
              We’re refining your pool. Try adjusting your preferences or check back shortly.
            </p>
            <button
              onClick={() => navigate('/preferences')}
              className="px-5 py-2 rounded-lg bg-[#D4AF37] text-black font-semibold"
            >
              Update Preferences
            </button>
          </div>
        )}

      <div className="px-6 md:px-12 py-10 md:py-14">
        {/* Presence intro prompt — auto-shows once after first Discovery visit */}
        {showPresencePrompt && (
          <div className="mb-7 glass-strong rounded-2xl p-5 flex items-start gap-4 animate-fade-up">
            {/* Presence icon */}
            <div
              className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
              style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.22)" }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" strokeWidth="1.6" stroke="#D4AF37">
                <circle cx="12" cy="12" r="2.5" fill="#D4AF37" strokeWidth="0" />
                <circle cx="12" cy="12" r="6.5" />
                <circle cx="12" cy="12" r="10.5" strokeOpacity="0.4" />
              </svg>
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] mb-0.5">New</div>
              <p className="font-display text-base leading-snug mb-1">Presence</p>
              <p className="text-sm text-white/55 leading-relaxed">
                See where alignment is strongest around you — always shown as
                area-level activity, never exact locations.
              </p>
              <div className="flex items-center gap-4 mt-3">
                <Link
                  to="/presence"
                  onClick={() => {
                    track("presence_prompt_cta_click");
                    setShowPresencePrompt(false);
                  }}
                  className="text-sm font-semibold text-[#F2DE94] hover:text-white transition-colors"
                >
                  Explore Presence →
                </Link>
                <button
                  onClick={() => {
                    track("presence_prompt_dismiss");
                    setShowPresencePrompt(false);
                  }}
                  className="text-sm text-white/35 hover:text-white/60 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>

            {/* Dismiss */}
            <button
              onClick={() => {
                track("presence_prompt_dismiss");
                setShowPresencePrompt(false);
              }}
              className="text-white/25 hover:text-white/60 transition-colors flex-shrink-0 p-1 -mt-0.5"
              aria-label="Dismiss Presence prompt"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current" strokeWidth="2" fill="none">
                <path d="m18 6-12 12M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Rediscovery chip — visible after prompt is dismissed or on re-visits */}
        {!showPresencePrompt &&
          localStorage.getItem("bm_presence_prompt_seen") === "true" && (
          <div className="mb-6 -mt-1 flex items-center gap-2">
            <svg
              viewBox="0 0 24 24"
              className="w-3 h-3 flex-shrink-0 text-white/35"
              fill="none"
              strokeWidth="1.6"
              stroke="currentColor"
            >
              <circle cx="12" cy="12" r="2.5" fill="currentColor" strokeWidth="0" />
              <circle cx="12" cy="12" r="6.5" />
            </svg>
            <Link
              to="/presence"
              className="text-[11px] uppercase tracking-[0.18em] text-white/35 hover:text-white/60 transition-colors"
            >
              Explore Presence
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
              Match pool · 12 curated
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">
              Your pool, <span className="italic text-gradient-gold">refined.</span>
            </h1>
            <p className="text-white/55 mt-2 text-sm md:text-base max-w-xl">
              Select exactly 7 to unlock your draft. You can revisit and swap any time.
            </p>
          </div>
          <button
            onClick={async () => {
              if (!canContinue) return;
              const tok = getToken();
              if (tok) {
                await fetch("/api/shortlist", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: "Bearer " + tok },
                  body: JSON.stringify({ prospect_ids: shortlist }),
                }).catch(() => {});
              }
              navigate("/select-seven");
            }}
            data-testid="go-to-draft"
            disabled={!canContinue}
            className={"rounded-full font-semibold px-5 py-3 self-start md:self-auto inline-flex items-center gap-2 transition-all " + (canContinue ? "bg-gradient-gold text-[#080B14] glow-gold-soft" : "bg-white/10 text-white/30 cursor-not-allowed")}
          >
            <span>{shortlist.length} / 7</span>
            <span className="h-4 w-px bg-black/30" />
            <span>Continue to Your 7 →</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-7">
          {["All", ...VIBE_TAGS].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`filter-${t.toLowerCase().replace(/\s/g, "-")}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm border transition-all ${
                filter === t
                  ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.08)] text-[#F2DE94]"
                  : "border-white/10 text-white/70 hover:border-white/25"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((p) => (
            <ProfileCard
              key={p.id}
              p={p}
              selectable
              state={shortlist.includes(p.id) ? "selected" : "idle"}
              onSelect={() => toggle(p.id)}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}
